import React from 'react';
import classnames from 'classnames';
import Text from '../../Text/Text';
import Icon from '../../Icon/Icon';
import Fade from '../../Transitions/Fade/Fade';

import './Checkbox.scss';

export interface CheckboxProps {
    /**
     * Ref checkbox
     */
    ref?: any;
    /**
     * Id attr checkbox
     */
    id?: string;
    /**
     * Name attr checkbox
     */
    name: string;
    /**
     * Value checkbox
     */
    value?: any;
    /**
     * Type attr checkbox
     * @default false
     */
    defaultChecked?: boolean;
    /**
     * Label attr checkbox
     */
    label?: string;
    /**
     * If has error
     * @default false
     */
    error?: boolean;
    /**
     * Read only
     * @default false
     */
    readonly?: boolean;
    /**
     * Disabled
     * @default false
     */
    disabled?: boolean;
    /**
     * Color style icon
     */
    color?: '' | 'primary' | 'secondary' | 'tertiary' | 'accent' | 'success' | 'warning' | 'error' | 'info';
    /**
     *  Icon
     * @default 'close'
     */
    icon?: string;
    /**
     *  Icon color
     */
    iconColor?: any;
    /**
     *  Icon size
     */
    iconSize?: number;
    /**
     * Class name styles
     */
    className?: string;
    /**
     * Checkbox class name styles
     */
    inputClassName?: string;
    /**
     * Style
     */
    style?: any;
    /**
     * Children node
     */
    children?: React.ReactNode;
    /**
     * Function called on change checkbox
     */
    onChange?(event?: any): any;
    /**
     * Other props
     */
    [otherProps: string]: any;
}

const Checkbox: React.FC<CheckboxProps> = React.forwardRef(
    (
        {
            id = 'checkbox',
            name,
            label,
            value,
            defaultChecked = false,
            error = false,
            readonly = false,
            disabled = false,
            color = 'primary',
            icon = 'check',
            iconColor = 'white',
            iconSize = 20,
            className,
            inputClassName,
            style,
            children,
            onChange = () => {},
            ...otherProps
        },
        ref
    ) => {
        const [checked, setChecked] = React.useState(defaultChecked);
        const classNames = classnames(['checkbox', { disabled }, className]);
        const checkboxClassNames = classnames([
            'checkbox__checkbox',
            { disabled, readonly, error: !!error },
            checked ? color : undefined,
            inputClassName,
        ]);

        React.useEffect(() => {
            setChecked(defaultChecked);
            onChange({ target: { value: defaultChecked, type: 'checkbox' }, value: defaultChecked });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [defaultChecked]);

        React.useEffect(() => {
            setChecked(value);
        }, [value]);

        const handleOnChange = (event: any) => {
            const { checked } = event?.target;
            setChecked(checked);
            onChange({ ...event, value: checked });
        };

        return (
            <label htmlFor={name} className={classNames} style={style}>
                <div className="checkbox__wrapper">
                    <div className="checkbox__content">
                        <div className={checkboxClassNames}>
                            <input
                                ref={ref}
                                id={id}
                                type="checkbox"
                                name={name}
                                value={value || ''}
                                checked={checked || false}
                                disabled={disabled}
                                onChange={handleOnChange}
                                {...otherProps}
                            />
                            {!!checked && (
                                <Fade>
                                    <Icon name={icon} color={iconColor} size={iconSize} className="checkbox__icon" />
                                </Fade>
                            )}
                        </div>
                    </div>
                    {label ? (
                        <Text tag="span" className="checkbox__label">
                            {label}
                        </Text>
                    ) : (
                        <div className="checkbox__label">{children}</div>
                    )}
                </div>
            </label>
        );
    }
);

export default Checkbox;
